import React, { useState, useRef, useEffect } from 'react';
import { Draggable } from '@hello-pangea/dnd';
import { FaEye } from 'react-icons/fa';
import userCircleIcon from '../../assets/UserCircle.svg';
import calendarDotsIcon from '../../assets/CalendarDots.svg';
import caretDoubleUpIcon from '../../assets/CaretDoubleUp.svg';
import chatCenteredIcon from '../../assets/ChatCentered.svg';
import plusCircleIcon from '../../assets/PlusCircle.svg';
import fileArrowUpIcon from '../../assets/FileArrowUp.svg';
import dotsIcon from '../../assets/dots.svg';
import pencilIcon from '../../assets/pencil.svg';
import trashIcon from '../../assets/trash.svg';
import subtaskExpandIcon from '../../assets/subtask-expand.svg';
import subtaskCollapseIcon from '../../assets/subtask-collapse.svg';

const TaskCard = ({ task, index, onClick, onShowTaskDetails, onEditTask, onDeleteTask, onOpenChat, onOpenUploadDocs, onCreateSubtask, onStatusChange }) => {
  const [showActionDropdown, setShowActionDropdown] = useState(false);
  const [subtasksExpanded, setSubtasksExpanded] = useState(false);
  const actionDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (actionDropdownRef.current && !actionDropdownRef.current.contains(event.target)) {
        setShowActionDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleActionDropdown = (e) => {
    e.stopPropagation();
    setShowActionDropdown(!showActionDropdown);
  };

  const calculateExpectedDays = (startDate, endDate) => {
    if (!startDate || !endDate) return '-';
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const formatDateRange = (start, end) => {
    if (!start || !end) return '-';
    const s = new Date(start);
    const e = new Date(end);
    const sStr = s.toLocaleDateString('en-US', { day: '2-digit', month: 'short' });
    const eStr = e.toLocaleDateString('en-US', { day: '2-digit', month: 'short' });
    const year = s.getFullYear().toString().substr(-2);
    return `${sStr} - ${eStr} ${year}`;
  };

  const getPriorityClass = (priority) => {
    const map = { low: 'priority-low', medium: 'priority-medium', high: 'priority-high', urgent: 'priority-urgent' };
    return map[priority] || '';
  };

  const getAssigneeName = (t) => {
    if (t.assigned_to_user) return t.assigned_to_user.name;
    if (t.assignee) return t.assignee.name;
    return 'Unassigned';
  };

  const renderCardBody = (t, isSubtask = false) => (
    <>
      {isSubtask && (
        <span className="subtask-parent-label">Subtask of {task.title}</span>
      )}

      <div className="task-card-header">
        <span className="task-card-title">
          {t.title?.length > 20 ? `${t.title.substring(0, 20)}...` : t.title}
        </span>
        {!isSubtask && (
          <div className="action-dropdown-container" ref={actionDropdownRef}>
            <button className="action-menu-btn" onClick={toggleActionDropdown} title="Actions">
              <img src={dotsIcon} alt="Actions" className="action-dots-icon" />
            </button>
            {showActionDropdown && (
              <div className="action-dropdown-menu">
                <div className="action-dropdown-item" onClick={(e) => { e.stopPropagation(); onShowTaskDetails?.(e, t); setShowActionDropdown(false); }}>
                  <FaEye className="action-dropdown-icon" />
                  <span>Show Task Details</span>
                </div>
                {t.permissions?.can_update && (
                  <div className="action-dropdown-item" onClick={(e) => { e.stopPropagation(); onEditTask?.(e, t); setShowActionDropdown(false); }}>
                    <img src={pencilIcon} alt="" className="action-dropdown-icon" />
                    <span>Edit Task</span>
                  </div>
                )}
                {t.permissions?.can_delete && (
                  <div className="action-dropdown-item delete-item" onClick={(e) => { e.stopPropagation(); onDeleteTask?.(e, t); setShowActionDropdown(false); }}>
                    <img src={trashIcon} alt="" className="action-dropdown-icon" />
                    <span>Delete Task</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="task-card-info">
        <div className="card-info-item">
          <img src={userCircleIcon} alt="" className="card-info-icon" />
          <span>{getAssigneeName(t)}</span>
        </div>
        <div className="card-info-item">
          <img src={calendarDotsIcon} alt="" className="card-info-icon" />
          <span>
            {formatDateRange(t.start_date, t.end_date || t.due_date)}
            ({calculateExpectedDays(t.start_date, t.end_date || t.due_date)} Days)
          </span>
        </div>
      </div>

      <div className="task-card-priority">
        <span className={`card-priority-badge ${getPriorityClass(t.priority)}`}>
          <img src={caretDoubleUpIcon} alt="" className="priority-caret-icon" />
          {t.priority?.toUpperCase()}
        </span>
      </div>

      <div className="task-card-footer">
        <div className="card-footer-left">
          {!isSubtask && task.subtasks_count > 0 && (
            <img
              src={subtasksExpanded ? subtaskCollapseIcon : subtaskExpandIcon}
              alt="toggle subtasks"
              className="card-action-icon"
              onClick={(e) => { e.stopPropagation(); setSubtasksExpanded(!subtasksExpanded); }}
            />
          )}
          {!isSubtask && (
            <img
              src={plusCircleIcon}
              alt="Create Subtask"
              className="card-action-icon"
              onClick={(e) => { e.stopPropagation(); onCreateSubtask?.(e, t); }}
            />
          )}
          {!isSubtask && onStatusChange && (
            <select 
              className="mobile-status-select" 
              value={t.status}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => {
                e.stopPropagation();
                onStatusChange(t.id, e.target.value);
              }}
            >
              <option value="todo">To Do</option>
              <option value="in_progress">In Progress</option>
              <option value="on_hold">Hold</option>
              <option value="reopen">Reopened</option>
              <option value="done">Completed</option>
            </select>
          )}
        </div>
        <div className="card-footer-right">
          <img
            src={fileArrowUpIcon}
            alt="Upload"
            className="card-action-icon"
            onClick={(e) => { e.stopPropagation(); onOpenUploadDocs?.(e, t); }}
          />
          <img
            src={chatCenteredIcon}
            alt="Chat"
            className="card-action-icon"
            onClick={(e) => { e.stopPropagation(); onOpenChat?.(e, t); }}
          />
        </div>
      </div>
    </>
  );

  return (
    <Draggable draggableId={task.id.toString()} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          className="task-card-wrapper"
        >
          {/* Parent card — drag handle here */}
          <div
            {...provided.dragHandleProps}
            className={`task-card ${snapshot.isDragging ? 'is-dragging' : ''}`}
            onClick={() => onClick && onClick(task)}
          >
            {renderCardBody(task, false)}
          </div>

          {/* Subtask cards below */}
          {subtasksExpanded && task.subtasks?.length > 0 && (
            <div className="subtask-cards-group">
              {task.subtasks.map((subtask) => (
                <div key={subtask.id} className="subtask-mini-card">
                  {renderCardBody(subtask, true)}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Draggable>
  );
};

export default TaskCard;
